import { inject, Injectable } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
} from '@web/common';
import { blank, Guid, valueOrDefault } from '@web/utils';
import { map, Observable } from 'rxjs';
import {
  PublicBoxQuestionnaireCollectionItemResponse,
  PublicBoxQuestionnaireDetailsResponse,
  PublicBoxQuestionnaireSummaryResponse,
} from '../response';
import { PublicBoxQuestionnaireFillRequest } from '../requests';

@Injectable({
  providedIn: 'root',
})
export class PublicBoxQuestionnaireApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(options: {
    request: CollectionRequest;
    cacheable?: boolean;
  }): Observable<
    CollectionResponse<PublicBoxQuestionnaireCollectionItemResponse>
  > {
    const path = 'box-questionnaire';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(options.request),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(options?.cacheable, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            PublicBoxQuestionnaireCollectionItemResponse,
          ),
        ),
      );
  }

  public fetchSummary(): Observable<PublicBoxQuestionnaireSummaryResponse> {
    const path = 'box-questionnaire/summary';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            PublicBoxQuestionnaireSummaryResponse,
          ),
        ),
      );
  }

  public fetchDetails(
    id: Guid,
  ): Observable<PublicBoxQuestionnaireDetailsResponse> {
    const path = `box-questionnaire/${id}`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            PublicBoxQuestionnaireDetailsResponse,
          ),
        ),
      );
  }

  public fill(options: {
    id: Guid;
    request: PublicBoxQuestionnaireFillRequest;
  }): Observable<PublicBoxQuestionnaireDetailsResponse> {
    const path = `box-questionnaire/${options.id}`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      options.request,
      PublicBoxQuestionnaireFillRequest,
    );

    return this.provider.http
      .put(url, body, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            PublicBoxQuestionnaireDetailsResponse,
          ),
        ),
      );
  }

  public endById(id: Guid): Observable<void> {
    const path = `box-questionnaire/${id}/end`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);

    return this.provider.http.post(url, {}).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
